// Money on screen (NFR-012, plan constraint 35). Amounts arrive as integer µUSD and are formatted
// by BigInt arithmetic on their digits — never by floating-point division — so no figure drifts.
// The wireframes' rules: dollars to the cent on screen with the exact six-decimal value beside it
// (tooltip), never rounded silently; a negative amount in parentheses and with a minus sign.

export type MicroUsd = number | bigint | string;

const MICRO_PER_USD = 1_000_000n;
const MINUS = "−";

// Refuses fractions and numbers already past float precision: those would have drifted already.
function toInteger(value: number | bigint | string): bigint {
  if (typeof value === "bigint") return value;
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value)) throw new RangeError("amount is not a safe integer");
    return BigInt(value);
  }
  if (!/^-?\d+$/.test(value)) throw new RangeError("amount is not an integer");
  return BigInt(value);
}

/** The µUSD amount as a BigInt. */
export function toMicro(value: MicroUsd): bigint {
  return toInteger(value);
}

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Half away from zero at the requested number of decimals, on the magnitude.
function roundMagnitude(magnitude: bigint, decimals: 2 | 6): bigint {
  const step = 10n ** BigInt(6 - decimals);
  return ((magnitude + step / 2n) / step) * step;
}

function dollars(magnitude: bigint, decimals: 2 | 6): string {
  const units = magnitude / MICRO_PER_USD;
  const fraction = (magnitude % MICRO_PER_USD).toString().padStart(6, "0").slice(0, decimals);
  return `$${group(units.toString())}.${fraction}`;
}

function signed(text: string, negative: boolean): string {
  return negative ? `(${MINUS}${text})` : text;
}

/** `$1,234.57`, or `(−$1,234.57)` when negative; `decimals: 6` gives the exact value. */
export function formatUsd(value: MicroUsd, decimals: 2 | 6 = 2): string {
  const micro = toMicro(value);
  const negative = micro < 0n;
  const magnitude = roundMagnitude(negative ? -micro : micro, decimals);
  return signed(dollars(magnitude, decimals), negative && magnitude !== 0n);
}

export interface MoneyDisplay {
  /** To the cent, for the cell. */
  text: string;
  /** All six decimals, for the tooltip and screen readers. */
  exact: string;
  negative: boolean;
}

export function money(value: MicroUsd): MoneyDisplay {
  const micro = toMicro(value);
  return { text: formatUsd(micro, 2), exact: formatUsd(micro, 6), negative: micro < 0n };
}

/** Integer basis points as a percentage: 1234 → `12.34%`, −50 → `(−0.50%)`. */
export function formatBasisPoints(bp: number | bigint): string {
  const value = toInteger(bp);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const text = `${group((magnitude / 100n).toString())}.${(magnitude % 100n).toString().padStart(2, "0")}%`;
  return signed(text, negative);
}

/** numerator / denominator in basis points, half away from zero; null when the denominator is 0. */
export function ratioBasisPoints(numerator: MicroUsd, denominator: MicroUsd): bigint | null {
  const n = toMicro(numerator) * 10_000n;
  const d = toMicro(denominator);
  if (d === 0n) return null;
  const negative = n < 0n !== d < 0n;
  const an = n < 0n ? -n : n;
  const ad = d < 0n ? -d : d;
  const q = (2n * an + ad) / (2n * ad);
  return negative ? -q : q;
}

export function sumMicro(values: readonly MicroUsd[]): bigint {
  return values.reduce<bigint>((total, value) => total + toMicro(value), 0n);
}

/**
 * An operator's dollar input as µUSD: `1234.5`, `$1,234.50`, `-3`, `(−$3.25)`; at most six
 * decimals (a seventh would be a silent rounding). null when it is not an amount.
 */
export function parseUsd(input: string): bigint | null {
  let text = input.trim().replace(/,/g, "");
  let negative = false;
  const parenthesised = /^\((.*)\)$/.exec(text);
  if (parenthesised) {
    negative = true;
    text = (parenthesised[1] as string).trim();
  }
  // The app's own rendering, `(−$3.00)`, carries both marks; either one alone also means negative.
  if (/^[-−]/.test(text)) {
    negative = true;
    text = text.slice(1);
  }
  const match = /^\$?(\d+)(?:\.(\d{1,6}))?$/.exec(text);
  if (!match) return null;
  const micro = BigInt(match[1] as string) * MICRO_PER_USD + BigInt((match[2] ?? "").padEnd(6, "0"));
  return negative ? -micro : micro;
}

// ---- percentages at the edge (the redesign shows % where the API speaks basis points) ----

/**
 * An operator's percentage as integer basis points: `30` → 3000, `12.5` → 1250, `0.01` → 1,
 * `-2` → −200; a trailing `%` and spaces are allowed. null when it is not a percentage or would need
 * more than two decimals (a finer value would be silently rounded).
 */
export function parsePercentToBp(input: string): number | null {
  const text = input.trim().replace(/\s*%$/, "").replace(/^−/, "-");
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const bp = Number(match[2]) * 100 + Number((match[3] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(bp)) return null;
  return match[1] === "-" && bp !== 0 ? -bp : bp;
}

/** Basis points as the text of a % input: 3000 → `30`, 1250 → `12.5`, 1 → `0.01`, −200 → `-2`. */
export function bpToPercentInput(bp: number): string {
  const negative = bp < 0;
  const magnitude = Math.abs(Math.trunc(bp));
  const whole = Math.trunc(magnitude / 100);
  const fraction = (magnitude % 100).toString().padStart(2, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/** Basis points for display with a fixed number of decimals: 3000 → `30.0%` (default one decimal). */
export function formatPercentBp(bp: number | bigint | null, decimals: 0 | 1 | 2 = 1): string {
  if (bp === null) return "—";
  const value = Number(bp) / 100;
  const text = Math.abs(value).toFixed(decimals);
  return value < 0 ? `${MINUS}${text}%` : `${text}%`;
}

/**
 * Margin (share of the bill kept) from a markup on cost, both in basis points, half away from
 * zero: a 30 % markup (3000) is a 23.08 % margin (2308). null for a markup of −100 % or less.
 */
export function marginBpFromMarkupBp(markupBp: number): number | null {
  const denominator = 10_000 + markupBp;
  if (denominator <= 0) return null;
  const numerator = markupBp * 10_000;
  const q = Math.abs(numerator) / denominator;
  const rounded = Math.floor(q + 0.5);
  return numerator < 0 ? -rounded : rounded;
}

/**
 * Markup on cost from a profit and a cost, in basis points (profit ÷ cost); null when the cost is
 * zero. The same half-away-from-zero BigInt ratio as the margin, so the two sit side by side.
 */
export function markupBpFrom(profit: MicroUsd, cost: MicroUsd): bigint | null {
  return ratioBasisPoints(profit, cost);
}

/** A dollar figure for an axis or a compact card: `$0`, `$950`, `$12.5k`, `$25k`, `$1.2M`. */
export function formatUsdCompact(dollars: number): string {
  const negative = dollars < 0;
  const magnitude = Math.abs(dollars);
  let text: string;
  if (magnitude >= 1_000_000) text = `$${trimZeros((magnitude / 1_000_000).toFixed(1))}M`;
  else if (magnitude >= 1_000) text = `$${trimZeros((magnitude / 1_000).toFixed(magnitude >= 10_000 ? 0 : 1))}k`;
  else text = `$${trimZeros(magnitude.toFixed(magnitude >= 10 ? 0 : 2))}`;
  return negative ? `${MINUS}${text}` : text;
}

function trimZeros(text: string): string {
  return text.includes(".") ? text.replace(/\.?0+$/, "") : text;
}

/** µUSD as a float number of dollars, for chart geometry only (never for a printed amount). */
export function microToDollars(value: MicroUsd): number {
  const micro = toMicro(value);
  const whole = micro / MICRO_PER_USD;
  const fraction = micro % MICRO_PER_USD;
  return Number(whole) + Number(fraction) / 1_000_000;
}
